import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Watermark } from "../components/Watermark";
import { TranscodingAdmin } from "../components/TranscodingAdmin";
import { GroupsAdmin } from "../components/GroupsAdmin";
import { BetaFeaturesAdmin } from "../components/BetaFeaturesAdmin";
import { BranchModeratorsAdmin } from "../components/BranchModeratorsAdmin";
import { TrashAdmin } from "../components/TrashAdmin";
import { LogoBranding } from "../components/LogoBranding";
import { apiFetch } from "../lib/api";
import { useActualMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import {
  setPreviewRole,
  usePreviewRole,
} from "../hooks/previewRoleStore";
import {
  ROLES,
  SUGGESTED_AI_TURN_LIMIT_30D,
  WATERMARK_POSITIONS,
  FAMILY_THEMES,
  can,
} from "@family/shared";
import type {
  FamilySettings,
  FamilyTheme,
  Role,
  UpdateFamilySettingsRequest,
  WatermarkConfig,
  WatermarkPosition,
} from "@family/shared";
import { PALETTES } from "../lib/palettes";

export function SettingsPage() {
  const queryClient = useQueryClient();
  const previewRole = usePreviewRole();

  // Use the real me here so an admin previewing as viewer still passes the
  // admin gate and can exit the preview.
  const meQuery = useActualMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const settingsQuery = useQuery<FamilySettings>({
    queryKey: ["settings", slug],
    queryFn: () => apiFetch<FamilySettings>(`/families/${slug}/settings`),
    enabled: Boolean(slug) && primary?.role === "admin",
  });

  const [draft, setDraft] = useState<WatermarkConfig | null>(null);
  useEffect(() => {
    if (settingsQuery.data && !draft) {
      setDraft(settingsQuery.data.watermark);
    }
  }, [settingsQuery.data, draft]);

  const save = useMutation<
    FamilySettings,
    Error,
    UpdateFamilySettingsRequest
  >({
    mutationFn: (body) =>
      apiFetch<FamilySettings>(`/families/${slug}/settings`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<FamilySettings>(["settings", slug], data);
      // Invalidate /me so the timeline picks up the new watermark.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !can(primary.role, "settings:edit")) {
    return <Navigate to="/" replace />;
  }

  if (settingsQuery.isLoading || !draft) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading settings…</p>
        </main>
      </>
    );
  }

  function patch<K extends keyof WatermarkConfig>(
    key: K,
    value: WatermarkConfig[K],
  ) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify(settingsQuery.data?.watermark);

  const previewVars = {
    user: meQuery.data!.user.displayName ?? meQuery.data!.user.email ?? "You",
    family: primary.familyName,
    phone: meQuery.data!.user.phone,
    email: meQuery.data!.user.email,
  };

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Settings — {primary.familyName}</h1>

        <section className="stack">
          <h2>Preview as</h2>
          <p className="text-muted">
            See the site the way another role would see it. Only changes what
            you see in this browser — your actual permissions are unchanged.
          </p>
          <div
            style={{
              display: "flex",
              gap: "var(--s-2)",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className={previewRole === null ? "" : "btn-secondary"}
              onClick={() => setPreviewRole(null)}
            >
              Admin (real)
            </button>
            {ROLES.filter((r) => r !== "admin").map((r: Role) => (
              <button
                key={r}
                type="button"
                className={previewRole === r ? "" : "btn-secondary"}
                onClick={() => setPreviewRole(r)}
              >
                Preview as {r}
              </button>
            ))}
          </div>
        </section>

        <ThemePicker
          current={settingsQuery.data?.theme ?? "classic"}
          onSelect={(theme) => save.mutate({ theme })}
          saving={save.isPending}
        />

        <section className="stack">
          <h2>Home Page Layout</h2>
          <p className="text-muted">
            Choose what members see on the home page after signing in.
          </p>
          <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
            {([
              { value: "timeline", label: "Timeline", desc: "Posts feed with search" },
              { value: "landing", label: "Landing", desc: "Editorial welcome with navigation cards" },
              { value: "both", label: "Both", desc: "Landing section + timeline below" },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => save.mutate({ homeLayout: opt.value })}
                disabled={save.isPending}
                style={{
                  flex: "1 1 140px",
                  padding: "12px 16px",
                  border: (settingsQuery.data?.homeLayout ?? "timeline") === opt.value
                    ? "2px solid var(--c-accent, var(--c-blue))"
                    : "1px solid var(--c-border-strong)",
                  borderRadius: 8,
                  background: "var(--c-bg-subtle, var(--c-bg))",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "var(--c-text)",
                }}
              >
                <strong style={{ fontSize: "0.9rem", color: "var(--c-text)" }}>{opt.label}</strong>
                <p style={{ fontSize: "0.8rem", margin: "4px 0 0", color: "var(--c-text-muted)" }}>{opt.desc}</p>
              </button>
            ))}
          </div>
          {(settingsQuery.data?.homeLayout ?? "timeline") === "both" && (
            <label className="stack" style={{ gap: 4, maxWidth: 420 }}>
              <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                Welcome section for new members
              </span>
              <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                Days after a member finishes onboarding that the welcome
                section stays on their home page. 0 hides it automatically;
                it stays reachable from the menu as "About this site".
              </span>
              <input
                type="number"
                min={0}
                max={365}
                key={settingsQuery.data?.welcomeDays ?? 7}
                defaultValue={settingsQuery.data?.welcomeDays ?? 7}
                disabled={save.isPending}
                style={{ width: 100 }}
                onBlur={(e) => {
                  const v = Number(e.currentTarget.value);
                  if (
                    Number.isInteger(v) &&
                    v >= 0 &&
                    v <= 365 &&
                    v !== (settingsQuery.data?.welcomeDays ?? 7)
                  ) {
                    save.mutate({ welcomeDays: v });
                  }
                }}
              />
            </label>
          )}
        </section>

        <section className="stack">
          <h2>Watermark</h2>
          <p className="text-muted">
            A small overlay shown on every photo, customised per viewer.
            Deters leaks by attributing any screenshot to the viewer who was
            logged in when it was taken.
          </p>

          <div className="card stack">
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(e) => patch("enabled", e.target.checked)}
                  style={{
                    width: "auto",
                    minHeight: 0,
                    padding: 0,
                    margin: 0,
                  }}
                />
                <span>Show watermark on photos</span>
              </label>
            </div>

            <div>
              <label htmlFor="wm-text">Text</label>
              <input
                id="wm-text"
                type="text"
                value={draft.text}
                onChange={(e) => patch("text", e.target.value)}
                disabled={!draft.enabled}
              />
              <p className="text-subtle" style={{ fontSize: "0.85rem", marginTop: 4 }}>
                Placeholders:{" "}
                <code>{"{user}"}</code>, <code>{"{family}"}</code>,{" "}
                <code>{"{phone}"}</code>, <code>{"{email}"}</code>
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "var(--s-3)",
              }}
            >
              <div>
                <label htmlFor="wm-position">Position</label>
                <select
                  id="wm-position"
                  className="select-skapa"
                  value={draft.position}
                  onChange={(e) =>
                    patch("position", e.target.value as WatermarkPosition)
                  }
                  disabled={!draft.enabled}
                >
                  {WATERMARK_POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wm-opacity">Opacity ({draft.opacity.toFixed(2)})</label>
                <input
                  id="wm-opacity"
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draft.opacity}
                  onChange={(e) =>
                    patch("opacity", parseFloat(e.target.value))
                  }
                  disabled={!draft.enabled}
                  style={{
                    width: "100%",
                    minHeight: 0,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                  }}
                />
              </div>
              <div>
                <label htmlFor="wm-size">Font size ({draft.fontSize}px)</label>
                <input
                  id="wm-size"
                  type="range"
                  min={8}
                  max={120}
                  step={1}
                  value={draft.fontSize}
                  onChange={(e) =>
                    patch("fontSize", parseInt(e.target.value, 10))
                  }
                  disabled={!draft.enabled}
                  style={{
                    width: "100%",
                    minHeight: 0,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                  }}
                />
              </div>
            </div>
          </div>

          <h3 style={{ fontSize: "1rem" }}>Preview</h3>
          <div
            style={{
              position: "relative",
              aspectRatio: "4 / 3",
              maxWidth: 480,
              background: "linear-gradient(135deg, #334155, #0f172a)",
              borderRadius: 4,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontSize: "0.9rem",
            }}
          >
            <span>[sample photo]</span>
            <Watermark config={draft} variables={previewVars} />
          </div>

          {save.error && <p className="text-error">{save.error.message}</p>}

          <div style={{ display: "flex", gap: "var(--s-3)" }}>
            <button
              onClick={() => save.mutate({ watermark: draft })}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDraft(settingsQuery.data?.watermark ?? null)}
              disabled={!dirty || save.isPending}
            >
              Reset
            </button>
          </div>
        </section>

        <section className="stack">
          <h2>Groups</h2>
          <p className="text-muted">
            Create groups to control who sees posts and events. Manual groups let you
            pick members. Branch groups auto-populate from the family tree.
          </p>
          <GroupsAdmin familySlug={slug!} />
        </section>

        <section className="stack">
          <h2>Logo</h2>
          <p className="text-muted">
            Upload your family's own logo. The web app stores both a light and a
            dark variant; the right one is picked based on where it's shown.
          </p>
          <LogoBranding familySlug={slug!} membership={primary} />
        </section>

        <section className="stack">
          <h2>Cover Photo</h2>
          <p className="text-muted">
            The featured photo shown on the landing page. Pick any image — ideally a group family photo.
          </p>
          <CoverPhotoUpload familySlug={slug!} currentUrl={primary.coverUrl ?? null} />
        </section>

        <section className="stack">
          <h2>Family AI allowance</h2>
          <p className="text-muted" style={{ margin: 0, maxWidth: "62ch" }}>
            How many questions the family assistant will answer in any rolling
            30 days. Counted in questions rather than tokens because that is
            the number anyone can reason about; the tokens are on the{" "}
            <Link to="/stats">Stats page</Link>.
          </p>
          <p className="text-muted" style={{ margin: 0, maxWidth: "62ch" }}>
            Leave it empty for no limit, which is how it has always worked.
            When the allowance runs out the assistant says so and points at an
            admin — it does not pretend to be busy. Questions it can answer
            from the family's own records without asking the model are always
            free and are never blocked.
          </p>
          <div style={{ display: "flex", gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap" }}>
            <label htmlFor="ai-cap">Questions per 30 days</label>
            <input
              id="ai-cap"
              type="number"
              min={0}
              step={10}
              placeholder="No limit"
              key={settingsQuery.data?.aiTurnLimit30d ?? "none"}
              defaultValue={settingsQuery.data?.aiTurnLimit30d ?? ""}
              disabled={save.isPending}
              style={{ width: 140 }}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                const next = raw === "" ? null : Math.max(0, Math.floor(Number(raw)));
                if (raw !== "" && !Number.isFinite(next as number)) return;
                if (next !== (settingsQuery.data?.aiTurnLimit30d ?? null)) {
                  save.mutate({ aiTurnLimit30d: next });
                }
              }}
            />
            {(settingsQuery.data?.aiTurnLimit30d ?? null) === null && (
              <button
                type="button"
                className="btn-secondary"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({ aiTurnLimit30d: SUGGESTED_AI_TURN_LIMIT_30D })
                }
                style={{ padding: "6px 14px", fontSize: "0.85rem", minHeight: 34 }}
              >
                Start with {SUGGESTED_AI_TURN_LIMIT_30D}
              </button>
            )}
          </div>
        </section>

        <section className="stack">
          <h2>Beta features</h2>
          <p className="text-muted">
            Opt specific members into preview features. Admins must enrol
            themselves here too if they want to use the feature.
          </p>
          <BetaFeaturesAdmin familySlug={slug!} />
        </section>

        <section className="stack">
          <h2>Branch moderators</h2>
          <p className="text-muted">
            New sign-ups say which branch of the family they belong to. The
            people ticked for a branch get an email and a push with the
            applicant's parent names, so the right person vets them.
            Branches are the founder's children in the tree.
          </p>
          <BranchModeratorsAdmin familySlug={slug!} />
        </section>

        {can(primary.role, "trash:manage") && (
          <section className="stack">
            <h2>Trash</h2>
            <p className="text-muted">
              Posts, events, and news that members have deleted. Restore brings
              them back as if nothing happened. "Delete forever" is permanent.
            </p>
            <TrashAdmin familySlug={slug!} />
          </section>
        )}

        <section className="stack">
          <h2>Video Conversions</h2>
          <p className="text-muted">
            Videos uploaded in non-browser formats (MPG, AVI, etc.) are
            automatically converted to MP4. Cancel or delete stuck conversions here.
          </p>
          <TranscodingAdmin familySlug={slug!} />
        </section>
      </main>
    </>
  );
}

function ThemePicker({
  current,
  onSelect,
  saving,
}: {
  current: FamilyTheme;
  onSelect: (theme: FamilyTheme) => void;
  saving: boolean;
}) {
  return (
    <section className="stack">
      <h2>Color Palette</h2>
      <p className="text-muted">
        Choose a visual theme for the family site. All members will see the
        selected palette. Dark mode still works within each palette.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "var(--s-3)",
        }}
      >
        {FAMILY_THEMES.map((key) => {
          const palette = PALETTES[key];
          const isActive = key === current;
          return (
            <button
              key={key}
              type="button"
              onClick={() => !isActive && onSelect(key)}
              disabled={saving}
              style={{
                padding: 16,
                border: isActive
                  ? "2px solid var(--c-accent, var(--c-blue))"
                  : "1px solid var(--c-border)",
                borderRadius: 8,
                background: palette.light["--c-bg"],
                cursor: isActive ? "default" : "pointer",
                textAlign: "left",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {/* Color swatches */}
              <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: palette.light["--c-text"],
                  }}
                />
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: palette.light["--c-accent"],
                  }}
                />
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: palette.light["--c-bg-alt"],
                    border: "1px solid " + palette.light["--c-border"],
                  }}
                />
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: palette.light["--c-border-strong"],
                  }}
                />
              </div>
              <strong
                style={{
                  color: palette.light["--c-text"],
                  fontSize: "0.95rem",
                }}
              >
                {palette.name}
                {isActive && " ✓"}
              </strong>
              <p
                style={{
                  color: palette.light["--c-text-muted"],
                  fontSize: "0.8rem",
                  margin: "4px 0 0",
                  lineHeight: 1.3,
                }}
              >
                {palette.description}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function CoverPhotoUpload({
  familySlug,
  currentUrl,
}: {
  familySlug: string;
  currentUrl: string | null;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      // 1. Get a signed upload URL
      const { uploadUrl, mediaId } = await apiFetch<{ uploadUrl: string; mediaId: string }>(
        `/families/${familySlug}/media/upload-url`,
        {
          method: "POST",
          body: JSON.stringify({
            kind: "photo",
            mimeType: file.type,
            fileSize: file.size,
          }),
        },
      );

      // 2. Upload to GCS
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      // 3. Set as family cover
      await apiFetch(`/families/${familySlug}/settings`, {
        method: "PATCH",
        body: JSON.stringify({ coverMediaId: mediaId }),
      });

      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["settings", familySlug] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: "var(--s-3)", alignItems: "center", flexWrap: "wrap" }}>
      {currentUrl && (
        <img
          src={currentUrl}
          alt="Current cover"
          style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid var(--c-border)" }}
        />
      )}
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ minHeight: 34, padding: "6px 16px", fontSize: "0.85rem" }}
        >
          {uploading ? "Uploading…" : currentUrl ? "Replace cover" : "Upload cover photo"}
        </button>
        {error && <p className="text-error" style={{ fontSize: "0.8rem", marginTop: 4 }}>{error}</p>}
      </div>
    </div>
  );
}
